import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import Wrapper from "./_components/Wrapper";
import Screen from "./_components/Screen";
import ButtonBox from "./_components/ButtonBox";
import Button from "./_components/Button";

const BINARY_OPS = ["+", "-", "X", "/", "xʸ"];

const UNARY_BTNS = new Set([
  "sin", "cos", "tan", "sin⁻¹", "cos⁻¹", "tan⁻¹",
  "log", "ln", "eˣ", "x²", "√", "n!", "1/x",
]);

const CONSTANT_BTNS = new Set(["π", "e"]);

const SCI_BTNS = new Set([
  ...UNARY_BTNS, ...CONSTANT_BTNS, "xʸ",
]);

const compute = (a, b, op) => {
  if (op === "+") return a + b;
  if (op === "-") return a - b;
  if (op === "X") return a * b;
  if (op === "/") return b === 0 ? NaN : a / b;
  if (op === "xʸ") return Math.pow(a, b);
  return b;
};

const format = (value) => {
  if (!isFinite(value) || isNaN(value)) return "Error";
  return String(parseFloat(value.toPrecision(10)));
};

const factorial = (n) => {
  const int = Math.round(n);
  if (int < 0 || int > 170 || Math.abs(n - int) > 1e-9) return NaN;
  let r = 1;
  for (let i = 2; i <= int; i++) r *= i;
  return r;
};

const Calculator = (props) => {
  const { t } = useTranslation();

  const [display, setDisplay] = useState("0");
  const [expression, setExpression] = useState("");
  const [firstOperand, setFirstOperand] = useState(null);
  const [operator, setOperator] = useState(null);
  const [awaitingOperand, setAwaitingOperand] = useState(false);
  const [degMode, setDegMode] = useState(true);

  const btnValues = [
    ["sin",   "cos",   "tan",   "C",  "+-", "%", "/"],
    ["sin⁻¹", "cos⁻¹", "tan⁻¹", 7,    8,    9,   "X"],
    ["log",   "ln",    "eˣ",    4,    5,    6,   "-"],
    ["x²",    "xʸ",    "√",     1,    2,    3,   "+"],
    ["π",     "e",     "n!",    0,    ".",  degMode ? "DEG" : "RAD", "="],
  ];

  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const inputDigit = (digit) => {
    const d = String(digit);
    if (awaitingOperand) {
      setDisplay(d);
      setAwaitingOperand(false);
      return;
    }
    setDisplay((prev) => {
      if (prev === "Error") return d;
      if (prev === "0" && d !== "0") return d;
      if (prev === "-0" && d !== "0") return "-" + d;
      if (prev.length >= 15) return prev;
      return prev + d;
    });
  };

  const inputDecimal = () => {
    if (awaitingOperand) {
      setDisplay("0.");
      setAwaitingOperand(false);
      return;
    }
    setDisplay((prev) => (prev.includes(".") ? prev : prev + "."));
  };

  const handleOperator = (op) => {
    const current = parseFloat(display);
    if (firstOperand !== null && !awaitingOperand) {
      const result = compute(firstOperand, current, operator);
      const formatted = format(result);
      setDisplay(formatted);
      setFirstOperand(formatted === "Error" ? null : result);
      setExpression(`${formatted} ${op}`);
    } else {
      setFirstOperand(isNaN(current) ? null : current);
      setExpression(`${display} ${op}`);
    }
    setOperator(op);
    setAwaitingOperand(true);
  };

  const handleEquals = () => {
    if (operator === null || firstOperand === null) return;
    const current = parseFloat(display);
    const result = compute(firstOperand, current, operator);
    setExpression(`${firstOperand} ${operator} ${current} =`);
    setDisplay(format(result));
    setFirstOperand(null);
    setOperator(null);
    setAwaitingOperand(true);
  };

  const applyUnary = (fn, label) => {
    const num = parseFloat(display);
    if (isNaN(num)) return;
    const result = fn(num);
    setExpression(`${label}(${display})`);
    setDisplay(format(result));
    setAwaitingOperand(true);
  };

  const handleUnary = (btn) => {
    const num = parseFloat(display);

    switch (btn) {
      case "sin":
        return applyUnary((x) => Math.sin(degMode ? toRad(x) : x), "sin");
      case "cos":
        return applyUnary((x) => Math.cos(degMode ? toRad(x) : x), "cos");
      case "tan":
        return applyUnary((x) => Math.tan(degMode ? toRad(x) : x), "tan");
      case "sin⁻¹":
        return applyUnary((x) => degMode ? toDeg(Math.asin(x)) : Math.asin(x), "sin⁻¹");
      case "cos⁻¹":
        return applyUnary((x) => degMode ? toDeg(Math.acos(x)) : Math.acos(x), "cos⁻¹");
      case "tan⁻¹":
        return applyUnary((x) => degMode ? toDeg(Math.atan(x)) : Math.atan(x), "tan⁻¹");
      case "log":
        return applyUnary(Math.log10, "log");
      case "ln":
        return applyUnary(Math.log, "ln");
      case "eˣ":
        return applyUnary(Math.exp, "eˣ");
      case "x²":
        return applyUnary((x) => x * x, "x²");
      case "√":
        return applyUnary(Math.sqrt, "√");
      case "n!":
        return applyUnary(factorial, "n!");
      case "1/x":
        return applyUnary((x) => (x === 0 ? NaN : 1 / x), "1/x");
      case "π": {
        const val = format(Math.PI);
        setExpression("π");
        setDisplay(val);
        setAwaitingOperand(false);
        return;
      }
      case "e": {
        const val = format(Math.E);
        setExpression("e");
        setDisplay(val);
        setAwaitingOperand(false);
        return;
      }
      default:
        return;
    }
  };

  const handleReset = () => {
    setDisplay("0");
    setExpression("");
    setFirstOperand(null);
    setOperator(null);
    setAwaitingOperand(false);
  };

  const handleInvert = () => {
    setDisplay((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num) || num === 0) return prev;
      return format(num * -1);
    });
  };

  const handlePercent = () => {
    setDisplay((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num)) return prev;
      return format(num / 100);
    });
  };

  const getHandler = (btn) => {
    if (btn === "C")   return handleReset;
    if (btn === "+-")  return handleInvert;
    if (btn === "%")   return handlePercent;
    if (btn === "=")   return handleEquals;
    if (btn === "DEG" || btn === "RAD") return () => setDegMode((d) => !d);
    if (BINARY_OPS.includes(btn))  return () => handleOperator(btn);
    if (UNARY_BTNS.has(btn) || CONSTANT_BTNS.has(btn)) return () => handleUnary(btn);
    if (btn === ".") return inputDecimal;
    return () => inputDigit(btn);
  };

  return (
    <Wrapper>
      <Screen expression={expression} value={display} degMode={degMode} />
      <ButtonBox>
        {btnValues.flat().map((btn, i) => {
          const isSci = SCI_BTNS.has(btn);
          const isMode = btn === "DEG" || btn === "RAD";
          const className = [
            btn === "=" ? "equals" : "",
            isSci ? "sci" : "",
            isMode ? "mode" : "",
          ].filter(Boolean).join(" ");
          return (
            <Button
              key={i}
              className={className}
              value={btn}
              onClick={getHandler(btn)}
            />
          );
        })}
      </ButtonBox>
      <button className="white mt-2" onClick={props.handleCloseCalculator}>
        {t("close_calculator")}
      </button>
    </Wrapper>
  );
};

export default Calculator;
